import * as TooltipPrimitives from "@radix-ui/react-tooltip"
import React from "react"

import { cn } from "@utils/index"

interface TooltipProps
    extends Omit<TooltipPrimitives.TooltipContentProps, "content" | "onClick">,
    Pick<
        TooltipPrimitives.TooltipProps,
        "open" | "defaultOpen" | "onOpenChange" | "delayDuration"
    > {
    content: React.ReactNode
    onClick?: React.MouseEventHandler<HTMLButtonElement>
    side?: "bottom" | "left" | "top" | "right"
    showArrow?: boolean
}

const Tooltip = React.forwardRef<
    React.ElementRef<typeof TooltipPrimitives.Content>,
    TooltipProps
>(
    (
        {
            children,
            className,
            content,
            delayDuration,
            defaultOpen,
            open,
            onClick,
            onOpenChange,
            showArrow = true,
            side,
            sideOffset = 10,
            asChild,
            ...props
        }: TooltipProps,
        forwardedRef,
    ) => {
        return (
            <TooltipPrimitives.Provider delayDuration={150}>
                <TooltipPrimitives.Root
                    open={open}
                    defaultOpen={defaultOpen}
                    onOpenChange={onOpenChange}
                    delayDuration={delayDuration}
                    tremor-id="tremor-raw"
                >
                    <TooltipPrimitives.Trigger onClick={onClick} asChild={asChild}>
                        {children}
                    </TooltipPrimitives.Trigger>
                    <TooltipPrimitives.Portal>
                        <TooltipPrimitives.Content
                            ref={forwardedRef}
                            side={side}
                            sideOffset={sideOffset}
                            align="center"
                            className={cn(
                                "z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
                                className
                            )}
                            {...props}
                        >
                            {content}
                            {showArrow ? (
                                <TooltipPrimitives.Arrow
                                    className="border-none fill-primary"
                                    width={12}
                                    height={7}
                                    aria-hidden="true"
                                />
                            ) : null}
                        </TooltipPrimitives.Content>
                    </TooltipPrimitives.Portal>
                </TooltipPrimitives.Root>
            </TooltipPrimitives.Provider>
        )
    },
)

Tooltip.displayName = "Tooltip"

export { Tooltip, type TooltipProps }
